import React, { useEffect, useState } from 'react';
import { getCourseNotes, createNote, deleteNote } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { StickyNote, Trash2, Loader2, Clock } from 'lucide-react';

export default function MyNotesScreen(): React.JSX.Element {
  const toast = useToast();
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseId, setCourseId] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    setLoading(true);
    try {
      // Load notes from all courses by calling for each enrollment
      const enrollmentsRes = await fetch('/api/v1/me/enrollments', {
        headers: { Authorization: `Bearer ${localStorage.getItem('ecm_token')}` }
      }).then(r => r.json());
      const enrollmentList = enrollmentsRes.enrollments || [];
      const allNotes: any[] = [];
      for (const e of enrollmentList) {
        try {
          const res = await getCourseNotes(e.course_id);
          allNotes.push(...(res.notes || []).map((n: any) => ({...n, course_title: e.course_title})));
        } catch {}
      }
      setNotes(allNotes);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load notes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    try {
      await deleteNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.show('Note deleted', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to delete note', 'error');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <StickyNote className="w-7 h-7 text-amber-500" />
        <h1 className="text-2xl font-bold text-slate-900">My Notes</h1>
      </div>

      {notes.length === 0 ? (
        <Card className="text-center py-12">
          <StickyNote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">No notes yet</h2>
          <p className="text-slate-500">Take timestamped notes while watching lectures.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {notes.map((note) => (
            <Card key={note.id} className="relative">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-500 mb-1">{note.course_title || 'Course'}</p>
                  {note.video_timestamp_seconds > 0 && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                      <Clock className="w-3 h-3" />
                      <span>{formatTime(note.video_timestamp_seconds)}</span>
                    </div>
                  )}
                  <p className="text-slate-800 whitespace-pre-wrap">{note.note}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(note.id)}
                  leftIcon={<Trash2 className="w-4 h-4 text-red-500" />}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
